import React, { memo, useEffect, useMemo } from 'react';
import { usePlotViewStore } from '../../../stores/plotViewStore';
import PlotCanvas from './PlotCanvas';
import PlotGridView from './PlotGridView';
import PlotViewToggle from './PlotViewToggle';

interface PlotItem {
  file_name: string;
  file_type: string;
  plot_json?: string;
}

interface PlotGridProps {
  plots: PlotItem[];
  sessionId?: string;
  userID?: string;
  tabId?: string;
}

const PlotGrid: React.FC<PlotGridProps> = ({ plots, sessionId, userID, tabId }) => {
  const { getTabLayout, setViewMode, initializeTabLayout } = usePlotViewStore();

  // Generate a stable tab ID if not provided
  const effectiveTabId = useMemo(() => {
    return tabId || `plot-tab-${plots.map((p) => p.file_name).sort().join('-')}`;
  }, [tabId, plots]);

  const layout = getTabLayout(effectiveTabId);
  const viewMode = layout.viewMode;

  // Initialize layout when plots change
  useEffect(() => {
    if (plots.length > 0) {
      initializeTabLayout(
        effectiveTabId,
        plots.map((p) => p.file_name)
      );
    }
  }, [effectiveTabId, plots, initializeTabLayout]);

  const handleViewModeChange = (mode: 'grid' | 'canvas') => {
    setViewMode(effectiveTabId, mode);
  };

  if (plots.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No plots available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <div className="text-sm font-medium text-gray-700">
          {plots.length} Plot{plots.length !== 1 ? 's' : ''}
        </div>
        <PlotViewToggle viewMode={viewMode} onViewModeChange={handleViewModeChange} />
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {viewMode === 'canvas' ? (
          <PlotCanvas
            plots={plots}
            tabId={effectiveTabId}
            sessionId={sessionId}
            userID={userID}
          />
        ) : (
          <PlotGridView
            plots={plots}
            tabId={effectiveTabId}
            sessionId={sessionId}
            userID={userID}
          />
        )}
      </div>
    </div>
  );
};

export default memo(PlotGrid);
